import { StrictMode, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";

const portfolioData = {
  name: "Narendra Bhatt",
  title: "Software Engineer | Mobile & Embedded Systems",
  contact: {
    email: "nirajbhatt1997@gmail.com",
    linkedin: "https://www.linkedin.com/in/oi-narendra/",
    github: "https://github.com/oi-narendra",
  },
  summary:
    "Software Engineer with 5+ years of experience in mobile and embedded systems. Hands-on work with Airspot Health's CO2 monitoring device, combining Flutter apps, ARM C firmware, and BLE connectivity. Skilled in building cloud-connected, user-focused products with AWS and Supabase.",
  experience: [
    {
      role: "Software Engineer",
      company: "Airspot Health",
      period: "09/2024 - Current",
      location: "Sydney, Remote",
      description: [
        "Re-architected a legacy native app to Flutter, enabling cross-platform scalability and reducing development time by 40%.",
        "Developed BLE protocols to communicate with ARM-based embedded firmware, achieving low-power, high-frequency data streaming.",
        "Worked with embedded C firmware on ARM microcontrollers, optimizing memory usage and real-time sensor sampling.",
        "Built a real-time CO2 mapping web app (Next.js + Supabase) to visualize sensor data across locations.",
      ],
    },
    {
      role: "Software Engineer",
      company: "No Bull Code Pvt. Ltd.",
      period: "01/2024 - 09/2024",
      location: "Remote",
      description: [
        "Architected a full-scale Flutter project from scratch, leading development from requirement gathering to final deployment.",
        "Integrated AWS Amplify for seamless cloud-based backend operations, ensuring reliability and scalability.",
        "Managed cross-country client communication, aligning development with business goals.",
        "Implemented CI/CD pipelines with Codemagic, reducing release cycles and enhancing development efficiency by 50%.",
      ],
    },
    {
      role: "Freelance Mobile Application Developer",
      company: "Excitation Inc.",
      period: "06/2023 - 08/2023",
      location: "Remote",
      description: [
        "Developed an MVP product using Flutter & React Native development, enabling rapid market testing and iteration.",
        "Integrated Code Push for seamless app updates, reducing deployment friction for end users.",
        "Worked closely with clients, refining UI/UX and ensuring feature alignment with business requirements.",
      ],
    },
    {
      role: "Junior Mobile App Developer",
      company: "Cocolabs Solution",
      period: "01/2020 - 05/2023",
      location: "Kathmandu, Nepal",
      description: [
        "Initiated a career in native Android development and later transitioned to Flutter for cross-platform projects.",
        "Developed an offline-first mobile app with SQLite and background sync.",
        "Implemented location-based features like proximity detection, area polygons, and dynamic form rendering.",
      ],
    },
  ],
  skills: {
    "Languages & Frameworks": [
      "Flutter",
      "Dart",
      "C/C++",
      "Python",
      "Next.js",
      "React Native",
      "Android/iOS",
    ],
    "Embedded & Connectivity": [
      "Bluetooth LE",
      "ARM C firmware",
      "OTA updates",
      "WebSockets",
      "REST",
    ],
    "Cloud & Tools": [
      "AWS (S3, Lambda)",
      "Supabase",
      "Firebase",
      "Codemagic",
      "Fastlane",
    ],
    "Architecture & Other": [
      "Clean Architecture",
      "MVVM",
      "BLoC",
      "SQLite",
      "PostgreSQL",
      "Git",
      "Figma",
      "CI/CD",
    ],
  },
  projects: [
    {
      title: "Flutter Package Development",
      name: "multi_dropdown",
      link: "https://pub.dev/packages/multi_dropdown",
      description:
        "Published multiple Flutter packages on pub.dev. The 'multi_dropdown' package achieved an average of 7000+ weekly downloads, widely recognized for its functionality and ease of use.",
    },
  ],
};

const Header = () => (
  <header className="header">
    <a href="#hero" className="header__logo">
      NARENDRA<span>.</span>
    </a>
    <nav className="header__nav">
      <a href="#about">About</a>
      <a href="#experience">Experience</a>
      <a href="#skills">Skills</a>
      <a href="#projects">Projects</a>
      <a href="#contact">Contact</a>
    </nav>
  </header>
);

const Hero = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [displayedText, setDisplayedText] = useState("");
  const [showCursor, setShowCursor] = useState(true);

  // Typing effect
  useEffect(() => {
    const fullText = portfolioData.title;
    let currentIndex = 0;

    const typingInterval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setDisplayedText(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        setShowCursor(false);
      }
    }, 100);

    return () => clearInterval(typingInterval);
  }, []);

  // Particle animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    // Code snippets from your actual tech stack
    const codeSnippets = [
      // Flutter
      "BlocProvider",
      "StreamBuilder",
      "Navigator.push()",
      "setState()",
      "Widget build()",
      "async/await",
      "FutureBuilder",
      "Provider.of<T>",
      "Riverpod",
      
      // React Native / Expo
      "expo-dev-client",
      "EAS Build",
      "expo-updates",
      "OTA.sync()",
      "useNavigation()",
      "StyleSheet.create",
      "AsyncStorage",
      "expo-sensors",
      
      // Native Android
      "onCreate()",
      "Intent()",
      "RecyclerView",
      "LiveData<T>",
      "ViewModel",
      "Room.database",
      "WorkManager",
      "BroadcastReceiver",
      
      // IoT & Embedded
      "nRF52840",
      "nrf_gpio_pin_set",
      "ARM Cortex-M4",
      "BLE_GAP_SCAN",
      "Arduino.h",
      "analogRead()",
      "Wire.begin()",
      "SPI.transfer()",
      "sd_ble_gap_adv",
      "nrf_drv_spi_init",
      "GPIOTE_IRQ",
      "RTC_CC_EVENT",
      "Keil µVision",
      "HAL_GPIO_Init()",
      "sensor.readTemp()",
      "I2C_Master_Tx",
      "BLE_GATT_WRITE",
      
      // Next.js / TypeScript
      "getServerSideProps",
      "next/router",
      "useEffect()",
      "TypeScript",
      "interface Props",
      "async function",
      "NextApiRequest",
      "middleware.ts",
      "getStaticProps",
      
      // AWS
      "s3.putObject()",
      "EventBridge.putEvents",
      "EC2.Instance",
      "VPC.Subnet",
      "AMI.CreateImage",
      "CloudFront.CDN",
      "Lambda.invoke()",
      "IAM.Role",
      "Route53.A",
      "ECS.Task",
      
      // Supabase / Postgres
      "supabase.from()",
      "PostgreSQL",
      "pg_query()",
      "RLS Policy",
      "supabase.auth",
      "realtime.subscribe",
      "pgvector",
      "JSONB",
      "pg_trigger",
    ];

    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    class CodeRainDrop {
      x: number;
      y: number;
      speed: number;
      text: string;
      fontSize: number;
      opacity: number;

      constructor() {
        this.x = Math.random() * canvasWidth;
        this.y = Math.random() * canvasHeight - canvasHeight;
        this.speed = Math.random() * 2 + 1;
        this.text =
          codeSnippets[Math.floor(Math.random() * codeSnippets.length)];
        this.fontSize = Math.random() * 8 + 10;
        this.opacity = Math.random() * 0.5 + 0.3;
      }

      update() {
        this.y += this.speed;
        if (this.y > canvasHeight) {
          this.y = -20;
          this.x = Math.random() * canvasWidth;
          this.text =
            codeSnippets[Math.floor(Math.random() * codeSnippets.length)];
        }
      }

      draw() {
        if (!ctx) return;
        ctx.font = `${this.fontSize}px 'Courier New', monospace`;
        ctx.fillStyle = `rgba(255, 215, 0, ${this.opacity})`;
        ctx.fillText(this.text, this.x, this.y);

        // Add trailing effect
        ctx.fillStyle = `rgba(255, 215, 0, ${this.opacity * 0.3})`;
        ctx.fillText(this.text, this.x, this.y - 20);
      }
    }

    const drops: CodeRainDrop[] = [];
    const dropCount = 25;
    for (let i = 0; i < dropCount; i++) {
      drops.push(new CodeRainDrop());
    }

    const animateCodeRain = () => {
      ctx.fillStyle = "rgba(18, 18, 18, 0.05)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      drops.forEach((drop) => {
        drop.update();
        drop.draw();
      });

      requestAnimationFrame(animateCodeRain);
    };

    animateCodeRain();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
    };
  }, []);

  // Particle animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    // Particle class
    class Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;

      constructor() {
        this.x = Math.random() * (canvas?.width || window.innerWidth);
        this.y = Math.random() * (canvas?.height || window.innerHeight);
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.radius = Math.random() * 2 + 1;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        const width = canvas?.width || window.innerWidth;
        const height = canvas?.height || window.innerHeight;

        if (this.x < 0 || this.x > width) this.vx *= -1;
        if (this.y < 0 || this.y > height) this.vy *= -1;
      }

      draw() {
        if (!ctx) return;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255, 215, 0, 0.8)";
        ctx.fill();
      }
    }

    // Create particles (fewer on mobile for better performance)
    const isMobile = window.innerWidth < 768;
    const particleCount = isMobile ? 40 : 80;
    const particles: Particle[] = [];
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    // Mouse tracking
    let mouseX = 0;
    let mouseY = 0;
    let mouseRadius = 150;

    const handleMouseMove = (e: MouseEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };
    window.addEventListener("mousemove", handleMouseMove);

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw particles
      particles.forEach((particle) => {
        particle.update();
        particle.draw();
      });

      // Connect particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 120) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(255, 215, 0, ${
              0.2 * (1 - distance / 120)
            })`;
            ctx.lineWidth = 1;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }

        // Connect to mouse
        const dx = particles[i].x - mouseX;
        const dy = particles[i].y - mouseY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < mouseRadius) {
          ctx.beginPath();
          ctx.strokeStyle = `rgba(255, 215, 0, ${
            0.3 * (1 - distance / mouseRadius)
          })`;
          ctx.lineWidth = 2;
          ctx.moveTo(particles[i].x, particles[i].y);
          ctx.lineTo(mouseX, mouseY);
          ctx.stroke();

          // Draw glow around mouse
          const gradient = ctx.createRadialGradient(
            mouseX,
            mouseY,
            0,
            mouseX,
            mouseY,
            mouseRadius
          );
          gradient.addColorStop(0, "rgba(255, 215, 0, 0.1)");
          gradient.addColorStop(1, "rgba(255, 215, 0, 0)");
          ctx.fillStyle = gradient;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
      }

      requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  // Generate binary/hex streams
  const generateBinaryStream = () => {
    return Array.from({ length: 12 }, () =>
      Math.random() > 0.5
        ? Math.floor(Math.random() * 2).toString()
        : Math.floor(Math.random() * 16)
            .toString(16)
            .toUpperCase()
    ).join(" ");
  };

  return (
    <section className="hero" id="hero">
      <canvas ref={canvasRef} id="particles-canvas"></canvas>

      {/* Binary/Hex streams in corners */}
      <div className="binary-stream top-left">
        {generateBinaryStream()}
        <br />
        {generateBinaryStream()}
        <br />
        {generateBinaryStream()}
        <br />
        {generateBinaryStream()}
      </div>
      <div className="binary-stream top-right">
        {generateBinaryStream()}
        <br />
        {generateBinaryStream()}
        <br />
        {generateBinaryStream()}
        <br />
        {generateBinaryStream()}
      </div>
      <div className="binary-stream bottom-left">
        {generateBinaryStream()}
        <br />
        {generateBinaryStream()}
        <br />
        {generateBinaryStream()}
        <br />
        {generateBinaryStream()}
      </div>
      <div className="binary-stream bottom-right">
        {generateBinaryStream()}
        <br />
        {generateBinaryStream()}
        <br />
        {generateBinaryStream()}
        <br />
        {generateBinaryStream()}
      </div>

      <div className="hero-content">
        <h1 className="animate-hero">
          Hi, I'm <span>{portfolioData.name}</span>
        </h1>
        <p className="animate-hero">
          {displayedText}
          {showCursor && <span className="typing-cursor"></span>}
        </p>
        <div className="hero__socials animate-hero">
          <a
            href={portfolioData.contact.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LinkedIn"
          >
            <i className="fab fa-linkedin"></i>
          </a>
          <a
            href={portfolioData.contact.github}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Github"
          >
            <i className="fab fa-github"></i>
          </a>
          <a href={`mailto:${portfolioData.contact.email}`} aria-label="Email">
            <i className="fas fa-envelope"></i>
          </a>
        </div>
        <a href="#about" className="scroll-indicator" aria-label="Scroll down">
          <div className="mouse">
            <div className="wheel"></div>
          </div>
          <div className="arrow">
            <span></span>
            <span></span>
          </div>
        </a>
      </div>
    </section>
  );
};

const About = () => (
  <section id="about" className="animate-on-scroll">
    <div className="container">
      <h2>About Me</h2>
      <p>{portfolioData.summary}</p>
    </div>
  </section>
);

const Experience = () => (
  <section id="experience">
    <div className="container">
      <h2>Professional Experience</h2>
      <div className="timeline">
        {portfolioData.experience.map((job, index) => (
          <div
            key={index}
            className={`timeline__item ${
              index % 2 === 0 ? "left" : "right"
            } animate-on-scroll`}
          >
            <div className="timeline__content">
              <h3>{job.role}</h3>
              <h4>
                {job.company} | {job.location}
              </h4>
              <p>{job.period}</p>
              <ul>
                {job.description.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Skills = () => (
  <section id="skills" className="animate-on-scroll">
    <div className="container">
      <h2>Core Skills</h2>
      <div className="skills-grid">
        {Object.entries(portfolioData.skills).map(([category, skillsList]) => (
          <div key={category} className="skill-category">
            <h3>{category}</h3>
            <div className="skills-list">
              {skillsList.map((skill) => (
                <span key={skill} className="skill-pill">
                  {skill}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Projects = () => (
  <section id="projects">
    <div className="container">
      <h2>Projects</h2>
      {portfolioData.projects.map((project, index) => (
        <div key={index} className="project-card animate-on-scroll">
          <h3>{project.title}</h3>
          <a href={project.link} target="_blank" rel="noopener noreferrer">
            {project.name} on pub.dev
          </a>
          <p>{project.description}</p>
        </div>
      ))}
    </div>
  </section>
);

const Contact = () => (
  <section id="contact" className="animate-on-scroll">
    <div className="container">
      <h2>Get In Touch</h2>
      <p>I'm currently open to new opportunities. Feel free to reach out!</p>
      <div className="contact-info">
        <a href={`mailto:${portfolioData.contact.email}`}>
          <i className="fas fa-envelope"></i> {portfolioData.contact.email}
        </a>
      </div>
      <div className="hero__socials">
        <a
          href={portfolioData.contact.linkedin}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="LinkedIn"
        >
          <i className="fab fa-linkedin"></i>
        </a>
        <a
          href={portfolioData.contact.github}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Github"
        >
          <i className="fab fa-github"></i>
        </a>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer>
    <p>
      &copy; {new Date().getFullYear()} Narendra Bhatt. All Rights Reserved.
    </p>
  </footer>
);

const App = () => {
  const codeRainCanvasRef = useRef<HTMLCanvasElement>(null);

  // 3D Particle Field - Site-wide background
  useEffect(() => {
    const canvas = codeRainCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    // 3D Particle with depth
    class Particle3D {
      x: number;
      y: number;
      z: number;
      vx: number;
      vy: number;
      vz: number;
      radius: number;

      constructor() {
        this.x = (Math.random() - 0.5) * 2000;
        this.y = (Math.random() - 0.5) * 2000;
        this.z = Math.random() * 2000;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.vz = (Math.random() - 0.5) * 2;
        this.radius = Math.random() * 2 + 1;
      }

      update(warpSpeed: number) {
        // Normal movement
        this.x += this.vx;
        this.y += this.vy;
        
        // Z movement accelerates with warp speed (scroll)
        const zSpeed = this.vz + warpSpeed;
        this.z += zSpeed;

        // Push particles away from center during warp
        if (warpSpeed > 5) {
          const centerX = 0;
          const centerY = 0;
          const dx = this.x - centerX;
          const dy = this.y - centerY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance > 0) {
            this.x += (dx / distance) * warpSpeed * 0.5;
            this.y += (dy / distance) * warpSpeed * 0.5;
          }
        }

        // Reset if too far - faster reset during warp
        if (this.z > 2000) {
          this.z = 0;
          this.x = (Math.random() - 0.5) * 2000;
          this.y = (Math.random() - 0.5) * 2000;
        }
        if (this.z < 0) this.z = 2000;
      }

      project() {
        const perspective = 600;
        const scale = perspective / (perspective + this.z);
        return {
          x: this.x * scale + canvasWidth / 2,
          y: this.y * scale + canvasHeight / 2,
          radius: this.radius * scale,
          opacity: 1 - this.z / 2000,
        };
      }
    }

    // Floating 3D geometric shapes
    class FloatingShape {
      x: number;
      y: number;
      z: number;
      rotation: number;
      rotationSpeed: number;
      size: number;
      vx: number;
      vy: number;
      type: 'cube' | 'triangle' | 'hexagon';

      constructor() {
        this.x = (Math.random() - 0.5) * 1500;
        this.y = (Math.random() - 0.5) * 1500;
        this.z = Math.random() * 1500;
        this.rotation = Math.random() * Math.PI * 2;
        this.rotationSpeed = (Math.random() - 0.5) * 0.02;
        this.size = Math.random() * 30 + 20;
        this.vx = (Math.random() - 0.5) * 0.3;
        this.vy = (Math.random() - 0.5) * 0.3;
        const types: ('cube' | 'triangle' | 'hexagon')[] = ['cube', 'triangle', 'hexagon'];
        this.type = types[Math.floor(Math.random() * types.length)];
      }

      update(warpSpeed: number) {
        this.x += this.vx;
        this.y += this.vy;
        this.rotation += this.rotationSpeed + (warpSpeed * 0.01);
        
        // Shapes move faster during warp
        this.z += 0.5 + (warpSpeed * 0.5);

        if (this.z > 1500) {
          this.z = 0;
          this.x = (Math.random() - 0.5) * 1500;
          this.y = (Math.random() - 0.5) * 1500;
        }
      }

      draw() {
        if (!ctx) return;
        const perspective = 600;
        const scale = perspective / (perspective + this.z);
        const screenX = this.x * scale + canvasWidth / 2;
        const screenY = this.y * scale + canvasHeight / 2;
        const size = this.size * scale;
        const opacity = (1 - this.z / 1500) * 0.3;

        ctx.save();
        ctx.translate(screenX, screenY);
        ctx.rotate(this.rotation);
        ctx.strokeStyle = `rgba(255, 215, 0, ${opacity})`;
        ctx.lineWidth = 2;

        if (this.type === 'cube') {
          ctx.strokeRect(-size / 2, -size / 2, size, size);
          ctx.strokeRect(-size / 3, -size / 3, size * 0.66, size * 0.66);
        } else if (this.type === 'triangle') {
          ctx.beginPath();
          ctx.moveTo(0, -size / 2);
          ctx.lineTo(size / 2, size / 2);
          ctx.lineTo(-size / 2, size / 2);
          ctx.closePath();
          ctx.stroke();
        } else {
          // Hexagon
          ctx.beginPath();
          for (let i = 0; i < 6; i++) {
            const angle = (Math.PI / 3) * i;
            const x = (size / 2) * Math.cos(angle);
            const y = (size / 2) * Math.sin(angle);
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          }
          ctx.closePath();
          ctx.stroke();
        }

        ctx.restore();
      }
    }

    const particles: Particle3D[] = [];
    const shapes: FloatingShape[] = [];
    const particleCount = 150;
    const shapeCount = 8;

    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle3D());
    }

    for (let i = 0; i < shapeCount; i++) {
      shapes.push(new FloatingShape());
    }

    let mouseX = canvas.width / 2;
    let mouseY = canvas.height / 2;
    let scrollVelocity = 0;
    let lastScrollY = window.scrollY;
    let warpSpeed = 0;

    const handleMouseMove = (e: MouseEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      scrollVelocity = (currentScrollY - lastScrollY) * 0.5;
      lastScrollY = currentScrollY;
      
      // Create warp speed effect on scroll
      warpSpeed += Math.abs(scrollVelocity) * 0.3;
      warpSpeed = Math.min(warpSpeed, 50); // Cap max warp speed
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("scroll", handleScroll, { passive: true });

    const animate = () => {
      // Fade effect - stronger during warp
      const fadeStrength = 0.1 + (warpSpeed * 0.01);
      ctx.fillStyle = `rgba(18, 18, 18, ${fadeStrength})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Decay warp speed
      warpSpeed *= 0.92;

      // Update and draw particles
      particles.forEach((particle) => {
        particle.update(warpSpeed);
        const projected = particle.project();

        // Enhanced particle glow during warp
        const glowSize = projected.radius + (warpSpeed * 0.1);
        const particleOpacity = projected.opacity * (0.8 + (warpSpeed * 0.02));

        // Draw particle with motion blur during warp
        if (warpSpeed > 2) {
          // Motion trail
          ctx.beginPath();
          ctx.arc(projected.x, projected.y, glowSize * 1.5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 215, 0, ${particleOpacity * 0.3})`;
          ctx.fill();
        }

        // Main particle
        ctx.beginPath();
        ctx.arc(projected.x, projected.y, projected.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 215, 0, ${particleOpacity})`;
        ctx.fill();

        // Connect nearby particles in 3D space
        particles.forEach((other) => {
          const otherProjected = other.project();
          const dx = projected.x - otherProjected.x;
          const dy = projected.y - otherProjected.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 100 && Math.abs(particle.z - other.z) < 200) {
            ctx.beginPath();
            ctx.moveTo(projected.x, projected.y);
            ctx.lineTo(otherProjected.x, otherProjected.y);
            const connectionOpacity = 0.1 * projected.opacity * otherProjected.opacity * (1 + warpSpeed * 0.05);
            ctx.strokeStyle = `rgba(255, 215, 0, ${connectionOpacity})`;
            ctx.lineWidth = 1 + (warpSpeed * 0.05);
            ctx.stroke();
          }
        });

        // Mouse interaction (reduced during warp)
        if (warpSpeed < 10) {
          const distToMouse = Math.sqrt(
            Math.pow(projected.x - mouseX, 2) + Math.pow(projected.y - mouseY, 2)
          );
          if (distToMouse < 150) {
            ctx.beginPath();
            ctx.moveTo(projected.x, projected.y);
            ctx.lineTo(mouseX, mouseY);
            ctx.strokeStyle = `rgba(255, 215, 0, ${0.2 * (1 - distToMouse / 150) * projected.opacity})`;
            ctx.lineWidth = 2;
            ctx.stroke();
          }
        }
      });

      // Update and draw 3D shapes
      shapes.forEach((shape) => {
        shape.update(warpSpeed);
        shape.draw();
      });

      requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll(".animate-on-scroll");
    sections.forEach((section) => observer.observe(section));

    const handleScroll = () => {
      const header = document.querySelector(".header");
      if (window.scrollY > 50) {
        header?.classList.add("scrolled");
      } else {
        header?.classList.remove("scrolled");
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      sections.forEach((section) => observer.unobserve(section));
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <>
      <canvas ref={codeRainCanvasRef} id="code-rain-canvas"></canvas>
      <Header />
      <main>
        <Hero />
        <About />
        <Experience />
        <Skills />
        <Projects />
        <Contact />
      </main>
      <Footer />
    </>
  );
};

const root = createRoot(document.getElementById("root")!);
root.render(
  <StrictMode>
    <App />
  </StrictMode>
);

